import {
	AutoCustomCompleteField,
	CustomDateControl,
	FormShell,
	MessageAlert,
	RBButton,
	RbToolbarActionButton,
	Timeline,
} from "@scmlatam/web-ui";
import { Icon } from "@mui/material";
import dayjs from "dayjs";
import type { AssignAccrualProfileDialogProps } from "../types/component";
import type { SelectOption } from "../types/hooks";

const INDEFINITE_DATE = dayjs("3000-01-01");

interface TimelineDate {
	isValid: () => boolean;
	toDate: () => Date;
}

const formatTimelineDate = (value: TimelineDate) => {
	if (!value?.isValid()) return "00/00/00";
	if (value.toDate().getFullYear() === 3000) return "Indefinido";
	return new Intl.DateTimeFormat(navigator.language, {
		day: "numeric",
		month: "numeric",
		year: "2-digit",
	}).format(value.toDate());
};

const getProfileLabel = (profileOptions: SelectOption[], profileId: string) =>
	profileOptions.find((profile) => profile.value === profileId)?.label || "-";

const AssignAccrualProfileDialog = ({
	formProps,
}: AssignAccrualProfileDialogProps) => {
	return (
		<FormShell
			open={formProps.openForm}
			title={formProps.title}
			size="l"
			onClose={formProps.handleCancel}
			loading={formProps.loadingData}
			alert={
				formProps.validationMessage ? (
					<MessageAlert
						type="warning"
						title={formProps.validationMessage}
						message=""
						className="rb-message-alert--form"
						actions={[
							{
								label: "OK",
								onClick: () => formProps.setValidationMessage(""),
							},
						]}
					/>
				) : formProps.showMessage ? (
					<MessageAlert
						type="warning"
						title={formProps.t("alerts.unsaved_changes.title")}
						message={formProps.t("alerts.unsaved_changes.message")}
						className="rb-message-alert--form"
						actions={[
							{
								label: formProps.t("alerts.unsaved_changes.action_button"),
								onClick: () => formProps.setShowMessage(false),
							},
							{
								label: formProps.t("alerts.unsaved_changes.cancel_button"),
								onClick: formProps.closeForm,
							},
						]}
					/>
				) : undefined
			}
			footer={
				<div className="flex flex-wrap items-center justify-end gap-3 p-3">
					<RBButton
						variant="secondary"
						size="m"
						disabled={formProps.loading}
						onClick={formProps.handleCancel}
					>
						{formProps.t("buttons.cancel")}
					</RBButton>
					<RBButton
						size="m"
						loading={formProps.loading}
						disabled={formProps.loadingData}
						onClick={formProps.submit}
					>
						{formProps.t("buttons.save")}
					</RBButton>
				</div>
			}
		>
			<div className="flex flex-col gap-6">
				<section className="flex flex-col gap-3">
					<span className="text-base font-semibold text-[#190007]">
						{formProps.selectedWorkerLabel}
					</span>
				</section>

				<Timeline
					segments={formProps.rows.flatMap((row) =>
						row.startDate?.isValid() && row.endDate?.isValid()
							? [{
								id: row.id,
								startDate: row.startDate,
								endDate: row.endDate,
								label: getProfileLabel(formProps.profileOptions, row.accrualProfileId),
							}]
							: [],
					).sort((first, second) => first.startDate.valueOf() - second.startDate.valueOf())}
					formatDate={formatTimelineDate}
					allowGaps
				/>

				<section className="flex flex-col gap-3">
					<div
						className="overflow-hidden rounded-lg border border-[#D8D6D2]"
						style={{ borderColor: "#D8D6D2" }}
					>
						<div
							className="items-center border-b border-[#D8D6D2] bg-[#F4F3F1] px-4 py-3 text-sm font-semibold text-[#5D5854]"
							style={{
								backgroundColor: "#F4F3F1",
								borderBottomColor: "#D8D6D2",
								display: "grid",
								gridTemplateColumns: "3.5rem minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)",
								columnGap: "16px",
							}}
						>
							<span />
							<span>{formProps.t("table.profile")}</span>
							<span>{formProps.t("table.start_date")}</span>
							<span>{formProps.t("table.end_date")}</span>
						</div>

						{formProps.rows.map((row) => {
							const errors = formProps.validationErrors[row.id];
							const isLastRow = row.id === formProps.rows.at(-1)?.id;

							return (
								<div
									key={row.id}
									className="items-start border-t border-[#D8D6D2] px-4 py-3"
									style={{
										display: "grid",
										gridTemplateColumns: "3.5rem minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)",
										columnGap: "16px",
										borderTopColor: "#D8D6D2",
									}}
								>
									<div className="flex h-11 items-center justify-center">
										<RbToolbarActionButton
											variant="icon"
											disabled={formProps.loading}
											onClick={() => formProps.removeRow(row.id)}
											icon={
												<Icon
													baseClassName="material-symbols-rounded"
													sx={{ color: "#190007", fontSize: "22px" }}
												>
													remove_circle
												</Icon>
											}
										/>
									</div>

									<AutoCustomCompleteField
										selectionMode="single"
										label={formProps.t("fields.profile")}
										placeholder={formProps.t("placeholder.select")}
										values={formProps.profileOptions}
										value={row.accrualProfileId}
										setValue={(accrualProfileId) =>
											formProps.updateRow(row.id, { accrualProfileId })
										}
										disabled={formProps.loading}
										white
										error={Boolean(errors?.accrualProfileId)}
										helperText={errors?.accrualProfileId || ""}
										search={{
											value: formProps.searchProfile,
											onChange: formProps.setSearchProfile,
											placeholder: formProps.t("placeholder.search"),
											mode: "local",
											emptyText: formProps.t("placeholder.empty"),
										}}
									/>

									<CustomDateControl
										label={formProps.t("fields.start_date")}
										initialValue={null}
										value={row.startDate}
										setValue={(startDate) => formProps.updateRow(row.id, { startDate })}
										disabled={formProps.loading}
										white
										fullWidth
										placeholder={formProps.t("placeholder.date")}
										error={Boolean(errors?.startDate)}
										helperText={errors?.startDate || ""}
									/>

									{isLastRow ? (
										<CustomDateControl
											label={formProps.t("fields.end_date")}
											value={row.endDate}
											setValue={(endDate) => formProps.updateRow(row.id, { endDate })}
											indefinite={row.endDate?.isSame(INDEFINITE_DATE, "day") ?? false}
											onIndefiniteChange={(indefinite) =>
												formProps.updateRow(row.id, {
													endDate: indefinite
														? INDEFINITE_DATE
														: row.startDate?.add(30, "day") ?? null,
												})
											}
											indefiniteProps={{
												indefiniteLabel: formProps.t("fields.indefinite"),
												showIndefiniteCheckbox: true,
											}}
											disabled={formProps.loading}
											white
											fullWidth
											placeholder={formProps.t("placeholder.date")}
											error={Boolean(errors?.endDate)}
											helperText={errors?.endDate || ""}
										/>
									) : (
										<CustomDateControl
											label={formProps.t("fields.end_date")}
											initialValue={null}
											value={row.endDate}
											setValue={(endDate) => formProps.updateRow(row.id, { endDate })}
											disabled={formProps.loading}
											white
											fullWidth
											placeholder={formProps.t("placeholder.date")}
											error={Boolean(errors?.endDate)}
											helperText={errors?.endDate || ""}
										/>
									)}
								</div>
							);
						})}
					</div>

					<div className="flex justify-start">
						<RBButton
							variant="secondary"
							size="s"
							disabled={formProps.loading}
							rightIcon={<Icon baseClassName="material-symbols-rounded">add</Icon>}
							onClick={formProps.addRow}
						>
							{formProps.t("buttons.add_profile")}
						</RBButton>
					</div>
				</section>
			</div>
		</FormShell>
	);
};

export { AssignAccrualProfileDialog };
